import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import authService from '@/services/authService'
import './Sidebar.scss'

/**
 * Sidebar Navigation Component
 * Features:
 * - Collapsible menu groups
 * - Icon-based navigation with submenu support
 * - User profile section
 * - Active state indicators based on current route
 * - Responsive mobile behavior
 * - Animated submenu expansion/collapse
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Sidebar open state (mobile)
 * @param {boolean} props.isCollapsed - Sidebar collapsed state (desktop)
 * @param {Function} props.onClose - Callback to close sidebar (mobile)
 * @param {Array} props.navItems - Navigation items array
 * @param {string} props.navItems[].label - Menu item label
 * @param {string} props.navItems[].path - Menu item path
 * @param {string} props.navItems[].icon - Menu item icon class
 * @param {Array} props.navItems[].submenu - Optional submenu items
 */
const Sidebar = ({ isOpen, isCollapsed, onClose, navItems = [] }) => {
	const location = useLocation()
	const [openMenus, setOpenMenus] = useState([])
	const currentUser = authService.getCurrentUser()

	// Get user display name and role
	const userName = currentUser
		? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.email
		: 'Guest User'
	const userRole = currentUser?.role?.name || 'User'

	// Generate avatar URL based on user name
	const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=5c8ed4&color=fff&size=50`

	// Auto-expand parent menu if child route is active
	useEffect(() => {
		if (navItems.length > 0) {
			const activeParents = navItems
				.filter(item => item.submenu?.some(sub => location.pathname === sub.path))
				.map(item => item.path)

			setOpenMenus(prev => {
				const newMenus = [...new Set([...prev, ...activeParents])]
				return newMenus
			})
		}
	}, [location.pathname, navItems])

	const toggleMenu = itemPath => {
		if (openMenus.includes(itemPath)) {
			setOpenMenus(openMenus.filter(path => path !== itemPath))
		} else {
			setOpenMenus([...openMenus, itemPath])
		}
	}

	const isActive = itemPath => {
		return location.pathname === itemPath
	}

	const hasActiveChild = item => {
		return item.submenu?.some(sub => location.pathname === sub.path)
	}

	return (
		<aside className={`sidebar ${isOpen ? 'sidebar--open' : ''} ${isCollapsed ? 'sidebar--collapsed' : ''}`}>
			<div className="sidebar__content">
				{/* User Profile Section */}
				<div className="sidebar__user">
					<img
						src={avatarUrl}
						alt={userName}
						className="sidebar__user-avatar"
					/>
					{!isCollapsed && (
						<div className="sidebar__user-info">
							<span className="sidebar__user-name">{userName}</span>
							<span className="sidebar__user-role">{userRole}</span>
						</div>
					)}
				</div>

				{/* Navigation Menu */}
				<nav className="sidebar__nav">
					<ul className="sidebar__menu">
						{navItems.map(item => {
							// Render section headers
							if (item.isSection) {
								return (
									<li key={item.id} className="sidebar__menu-section">
										{!isCollapsed && <span className="sidebar__menu-section-label">{item.label}</span>}
									</li>
								)
							}

							// Render menu items with submenu
							if (item.submenu) {
								return (
									<li key={item.path} className="sidebar__menu-item">
										<button
											className={`sidebar__menu-link ${openMenus.includes(item.path) ? 'sidebar__menu-link--open' : ''} ${hasActiveChild(item) ? 'sidebar__menu-link--active' : ''}`}
											onClick={() => !isCollapsed && toggleMenu(item.path)}
										>
											{item.icon && <i className={`${item.icon} sidebar__menu-icon`}></i>}
											{!isCollapsed && (
												<>
													<span className="sidebar__menu-text">{item.label}</span>
													<i className="icon-arrow-down sidebar__menu-arrow"></i>
												</>
											)}
										</button>
										{!isCollapsed && (
											<ul
												className={`sidebar__submenu ${openMenus.includes(item.path) ? 'sidebar__submenu--open' : ''}`}
											>
												{item.submenu.map((child, idx) => (
													<li key={idx}>
														<Link
															to={child.path}
															className={`sidebar__submenu-link ${isActive(child.path) ? 'sidebar__submenu-link--active' : ''}`}
															onClick={onClose}
														>
															<span>{child.label}</span>
														</Link>
													</li>
												))}
											</ul>
										)}
									</li>
								)
							}

							// Render regular menu items
							return (
								<li key={item.path} className="sidebar__menu-item">
									<Link
										to={item.path}
										className={`sidebar__menu-link ${isActive(item.path) ? 'sidebar__menu-link--active' : ''}`}
										onClick={onClose}
									>
										{item.icon && <i className={`${item.icon} sidebar__menu-icon`}></i>}
										{!isCollapsed && (
											<>
												<span className="sidebar__menu-text">{item.label}</span>
												{item.badge && <span className="sidebar__badge">{item.badge}</span>}
											</>
										)}
									</Link>
								</li>
							)
						})}
					</ul>
				</nav>

				{/* Footer */}
				{!isCollapsed && (
					<div className="sidebar__footer">
						<div className="sidebar__footer-info">
							<span className="sidebar__footer-version">Version 0.1.0</span>
							<a href="#" className="sidebar__footer-link">
								Help & Support
							</a>
						</div>
					</div>
				)}
			</div>
		</aside>
	)
}

export default Sidebar
